import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Skeleton, TextField } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import BaseModal, { BaseModalRef } from 'modals/BaseModal';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { Division } from 'modules/settings/interfaces/divisions';
import { createDivision, updateDivision } from 'modules/settings/slices/divisionsActions';

export interface DivisionModalProps {
    onSubmit?: () => void;
    onClose?: () => void;
}

const DivisionModal = (props: DivisionModalProps, ref: React.Ref<unknown>) => {
    const modal = useRef<BaseModalRef>(null);
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.divisions);
    const { register, handleSubmit, reset } = useForm();
    const [isOpen, setIsOpen] = useState(false);
    const [division, setDivision] = useState<Division | undefined>(undefined);
    const formId = 'division_create_form_' + nanoid();

    useEffect(() => {
        if (isOpen) {
            reset(division);
        }
    }, [isOpen]);

    const handleClose = () => {
        props.onClose?.();
    };

    const onSubmit = (data: any) => {
        if (division && division.id) {
            data.id = division.id;
            dispatch(updateDivision(data))
                .unwrap()
                .then(() => {
                    props.onSubmit?.();
                    modal?.current?.close();
                })
                .catch(() => {});
        } else {
            dispatch(createDivision(data))
                .unwrap()
                .then(() => {
                    props.onSubmit?.();
                    modal?.current?.close();
                })
                .catch(() => {});
        }
    };

    const open = (division: Division | undefined = undefined) => {
        if (division) {
            setDivision(division);
        } else {
            setDivision({
                name: '',
                number: '',
            });
        }
        modal?.current?.open();
    };

    const close = () => {
        modal?.current?.close();
    };

    useImperativeHandle(ref, () => ({
        open: (division?: Division | undefined) => open(division),
        close: () => close(),
    }));

    return (
        <>
            <BaseModal
                ref={modal}
                title={'Create cost code'}
                maxWidth={'xs'}
                open={isOpen}
                setOpen={setIsOpen}
                onClose={handleClose}
                actions={
                    <>
                        <Button color="primary" disableElevation onClick={close}>
                            Cancel
                        </Button>
                        <LoadingButton type="submit" form={formId} loading={loading} color="primary" disableElevation>
                            {division && division.id ? 'Update' : 'Create'}
                        </LoadingButton>
                    </>
                }
            >
                {isOpen ? (
                    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            id="name"
                            variant="outlined"
                            label="Name"
                            {...register('name')}
                            error={!!error?.errors?.name}
                            helperText={error?.errors?.name?.[0]}
                            autoFocus
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            id="number"
                            variant="outlined"
                            label="Number"
                            {...register('number')}
                            error={!!error?.errors?.number}
                            helperText={error?.errors?.number?.[0]}
                            autoFocus
                            fullWidth
                            required
                        />
                    </form>
                ) : (
                    <Skeleton variant="rounded" width={396} height={56} />
                )}
            </BaseModal>
        </>
    );
};

export default forwardRef(DivisionModal);
